import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { isDefined, isNotDefined, type CompanyId, type DashboardScopeId, type UserProfileData } from '@terros/common'
import { Group, Modal, Radio, SimpleGrid, Stack } from '@mantine/core'
import {
  getBrowsableDashboardTeams,
  getDashboardAudienceKinds,
  getDashboardAudienceSummary,
  getDashboardManageTier,
  isDashboardAudienceKind,
  needsAudienceSubSelection,
  resolveDashboardScopeId,
  useTeams,
  useTranslation,
  type DashboardAudience,
  type DashboardAudienceKind,
} from '@front/model'
import { ButtonCancel, TButton } from '@components'
import { DashboardAudienceSummary } from './DashboardAudienceSummary'
import { DashboardAudienceSubList, type AudienceSubKind } from './DashboardAudienceSubList'
import { DashboardAudienceOptionCard } from './DashboardAudienceOptionCard'
import { getAudienceOptionCopy } from './audienceOptions'

type Props = {
  opened: boolean
  /** "New Dashboard" for a create, "Duplicate Dashboard" when copying one. */
  title: string
  companyId: CompanyId | undefined
  companyName: string | undefined
  user: UserProfileData | undefined
  /** Preselects the audience matching the scope the list is filtered to. */
  initialAudience: DashboardAudience | undefined
  isSubmitting: boolean
  onClose: () => void
  onContinue: (scopeId: DashboardScopeId) => void
}

export function DashboardAudiencePickerModal({
  opened,
  title,
  companyId,
  companyName,
  user,
  initialAudience,
  isSubmitting,
  onClose,
  onContinue,
}: Props): ReactElement {
  const { t } = useTranslation()
  const tier = getDashboardManageTier(user?.permissionMap)
  const kinds = getDashboardAudienceKinds(tier)
  const [audience, setAudience] = useState<DashboardAudience | undefined>(initialAudience)

  // Reopening should offer the scope the list is currently on, not whatever was
  // half-chosen and abandoned last time.
  useEffect(() => {
    if (opened) setAudience(initialAudience)
  }, [initialAudience, opened])

  const { data: teams = [] } = useTeams({ enabled: opened && tier !== 'selfOnly' })
  const browsableTeams = useMemo(
    () => getBrowsableDashboardTeams({ teams, managedTeamIds: user?.teams?.manages, tier }),
    [teams, tier, user?.teams?.manages]
  )

  const scopeId = resolveDashboardScopeId({ audience, viewerUserId: user?.userId, companyId })
  const summary = getDashboardAudienceSummary({ audience, companyName })
  const subKind = getSubKind(audience?.kind)

  const handleKindChange = (value: string): void => {
    if (!isDashboardAudienceKind(value)) return
    setAudience({ kind: value })
  }

  return (
    <Modal opened={opened} size='lg' title={t(title)} onClose={onClose}>
      <Stack gap='md'>
        <Radio.Group
          label={t('Who are you building this dashboard for?')}
          value={audience?.kind ?? null}
          onChange={handleKindChange}
        >
          <SimpleGrid cols={{ base: 1, sm: 2 }} pt='sm' spacing='sm'>
            {kinds.map((kind) => {
              const copy = getAudienceOptionCopy(kind, companyName)
              return (
                <DashboardAudienceOptionCard
                  description={copy.description}
                  icon={copy.icon}
                  key={kind}
                  title={copy.title}
                  value={kind}
                />
              )
            })}
          </SimpleGrid>
        </Radio.Group>

        {isDefined(subKind) && (
          <DashboardAudienceSubList
            companyId={companyId}
            kind={subKind}
            selectedTarget={audience?.target}
            teams={browsableTeams}
            onSelect={(target) => setAudience({ kind: subKind, target })}
          />
        )}

        {isDefined(summary) && <DashboardAudienceSummary summary={summary} />}

        <Group justify='flex-end'>
          <ButtonCancel disabled={isSubmitting} onClick={onClose} />
          <TButton
            disabled={isNotDefined(scopeId)}
            loading={isSubmitting}
            onClick={() => isDefined(scopeId) && onContinue(scopeId)}
          >
            Continue
          </TButton>
        </Group>
      </Stack>
    </Modal>
  )
}

function getSubKind(kind: DashboardAudienceKind | undefined): AudienceSubKind | undefined {
  if (isNotDefined(kind) || !needsAudienceSubSelection(kind)) return undefined
  if (kind === 'team' || kind === 'role' || kind === 'person') return kind
  return undefined
}
